import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, ShieldCheck, X } from 'lucide-react';
import { AdminUser } from '../types';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const roleBadge = (role: string) => {
  const styles: Record<string, string> = {
    full: 'bg-green-100 text-green-700',
    readonly: 'bg-gray-100 text-gray-700',
    sales: 'bg-blue-100 text-blue-700',
    marketing: 'bg-purple-100 text-purple-700',
  };
  const labels: Record<string, string> = {
    full: 'Full',
    readonly: 'Read-only',
    sales: 'Sales',
    marketing: 'Marketing Manager',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${styles[role] ?? 'bg-gray-100 text-gray-700'}`}>
      {labels[role] ?? role}
    </span>
  );
};

interface FormState {
  fullName: string;
  email: string;
  password: string;
  adminRole: 'full' | 'readonly' | 'sales' | 'marketing';
}

const emptyForm: FormState = { fullName: '', email: '', password: '', adminRole: 'readonly' };

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await api.getAdminUsers();
      setUsers(data);
    } catch (err: any) {
      console.error('Error loading admin users:', err);
      setUsers([]);
      setLoadError(err?.response?.data?.message || 'Gagal memuat daftar admin.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const openCreate = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (user: AdminUser) => {
    setEditingUser(user);
    setForm({ fullName: user.fullName, email: user.email, password: '', adminRole: user.adminRole });
    setFormError(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingUser(null);
    setForm(emptyForm);
    setFormError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!form.fullName.trim()) { setFormError('Nama wajib diisi.'); return; }
    if (!editingUser && !form.email.trim()) { setFormError('Email wajib diisi.'); return; }
    if (!editingUser && !form.password.trim()) { setFormError('Password wajib diisi.'); return; }

    setSubmitting(true);
    try {
      if (editingUser) {
        const body: { fullName?: string; adminRole?: 'full' | 'readonly' | 'sales' | 'marketing'; password?: string } = {
          fullName: form.fullName.trim(),
          adminRole: form.adminRole,
        };
        if (form.password.trim()) body.password = form.password.trim();
        await api.updateAdminUser(editingUser.id, body);
      } else {
        await api.createAdminUser({
          fullName: form.fullName.trim(),
          email: form.email.trim(),
          password: form.password.trim(),
          adminRole: form.adminRole,
        });
      }
      closeForm();
      loadUsers();
    } catch (err: any) {
      setFormError(err?.response?.data?.message || 'Gagal menyimpan admin.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await api.deleteAdminUser(id);
      setDeletingId(null);
      loadUsers();
    } catch (err: any) {
      setDeleteError(err?.response?.data?.message || 'Gagal menghapus admin.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatDate = (dateString: string): string =>
    new Date(dateString).toLocaleDateString('id-ID', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-primary-600" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Kelola Admin</h1>
            <p className="mt-1 text-sm text-gray-500">Kelola akun admin panel dan hak aksesnya.</p>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700"
        >
          <Plus className="h-4 w-4" />
          Tambah Admin
        </button>
      </div>

      {loadError && (
        <div className="p-3 bg-red-50 text-red-700 rounded-md text-sm">{loadError}</div>
      )}

      {loading ? (
        <div className="bg-white shadow rounded-lg p-8">
          <LoadingSpinner size="md" text="Memuat daftar admin..." />
        </div>
      ) : users.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <h3 className="mt-2 text-sm font-medium text-gray-900">Belum ada admin</h3>
          <p className="mt-1 text-sm text-gray-500">Tambahkan admin baru untuk mulai.</p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Nama', 'Email', 'Role', 'Dibuat', 'Aksi'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map(user => (
                  <React.Fragment key={user.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{user.fullName}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{user.email}</td>
                      <td className="px-4 py-3">{roleBadge(user.adminRole)}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDate(user.createdAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEdit(user)}
                            className="text-primary-600 hover:text-primary-800"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => { setDeletingId(user.id); setDeleteError(null); }}
                            className="text-red-500 hover:text-red-700"
                            title="Hapus"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {deletingId === user.id && (
                      <tr className="bg-red-50">
                        <td colSpan={5} className="px-4 py-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="text-sm text-red-700">Hapus <strong>{user.fullName}</strong>? Tindakan ini tidak dapat dibatalkan.</span>
                              {deleteError && <div className="mt-1 text-sm text-red-700">{deleteError}</div>}
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => { setDeletingId(null); setDeleteError(null); }}
                                className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                              >
                                Batal
                              </button>
                              <button
                                onClick={() => handleDelete(user.id)}
                                disabled={deleteLoading}
                                className="px-3 py-1 text-sm text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
                              >
                                {deleteLoading ? 'Menghapus...' : 'Hapus'}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {editingUser ? 'Edit Admin' : 'Tambah Admin'}
              </h3>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {formError && (
                <div className="p-3 bg-red-50 text-red-700 rounded-md text-sm">{formError}</div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">Nama <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.fullName}
                  onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Email {!editingUser && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="email"
                  value={form.email}
                  disabled={!!editingUser}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Password {editingUser ? <span className="text-gray-400 text-xs">(kosongkan jika tidak diubah)</span> : <span className="text-red-500">*</span>}
                </label>
                <input
                  type="password"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder={editingUser ? 'Password baru...' : ''}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  value={form.adminRole}
                  onChange={e => setForm(f => ({ ...f, adminRole: e.target.value as 'full' | 'readonly' | 'sales' | 'marketing' }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="full">Full</option>
                  <option value="readonly">Read-only</option>
                  <option value="sales">Sales</option>
                  <option value="marketing">Marketing Manager</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={submitting}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {submitting && <LoadingSpinner size="sm" />}
                  {editingUser ? 'Simpan' : 'Tambah'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
